import { User, onAuthStateChanged } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { getFunctions } from 'firebase/functions'
import { useEffect } from 'react'
import useStore, { resetStore } from '../store/useStore'
import {
  VerifiedImpersonationSession,
} from '../types/Identity'
import { isImpersonationSession } from '../types/Impersonation'
import {
  activateEffectiveServices,
  primaryServices,
} from '../utils/firebaseServiceContext'
import {
  IMPERSONATION_SESSION_KEY,
  clearLocalImpersonation,
  getImpersonationAuth,
  getOrCreateImpersonationApp,
  getStoredImpersonationSessionId,
  stopImpersonation,
} from '../utils/impersonationService'
import { clearPatientQueryCache } from '../utils/queryClient'

interface ValidationResponse
  extends Omit<VerifiedImpersonationSession, 'verifiedAt'> {}

export type CrossTabIdentityAction = 'ignore' | 'clear-and-reload' | 'reload'

const terminalValidationCodes = new Set([
  'functions/failed-precondition',
  'functions/not-found',
  'functions/permission-denied',
  'functions/unauthenticated',
])

export const isTerminalValidationError = (error: unknown): boolean => {
  const code =
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof error.code === 'string'
      ? error.code
      : null
  if (code && terminalValidationCodes.has(code)) return true

  const message = error instanceof Error ? error.message : ''
  return (
    message === 'Secondary token has invalid impersonation claims.' ||
    message === 'Actor or session claim mismatch.' ||
    message === 'Validated actor, target, and session do not match.' ||
    message === 'Primary actor is signed out.'
  )
}

export const crossTabIdentityAction = (
  key: string | null,
  newValue: string | null,
  activeSessionId?: string,
): CrossTabIdentityAction => {
  if (key !== IMPERSONATION_SESSION_KEY) return 'ignore'
  if (!newValue) return 'clear-and-reload'
  return newValue === activeSessionId ? 'ignore' : 'reload'
}

export const validateIdentityBinding = async ({
  primaryUid,
  secondaryUser,
  storedSessionId,
  validateSession,
}: {
  primaryUid: string
  secondaryUser: Pick<User, 'uid' | 'getIdTokenResult'>
  storedSessionId: string
  validateSession: () => Promise<ValidationResponse>
}): Promise<VerifiedImpersonationSession> => {
  const token = await secondaryUser.getIdTokenResult()
  if (!isImpersonationSession(token.claims)) {
    throw new Error('Secondary token has invalid impersonation claims.')
  }
  if (
    token.claims.impersonationSessionId !== storedSessionId ||
    token.claims.impersonatorUid !== primaryUid
  ) {
    throw new Error('Actor or session claim mismatch.')
  }
  const data = await validateSession()
  if (
    data.sessionId !== storedSessionId ||
    data.actorUid !== primaryUid ||
    data.targetUid !== secondaryUser.uid
  ) {
    throw new Error('Validated actor, target, and session do not match.')
  }
  return { ...data, verifiedAt: Date.now() }
}

const validate = async (
  secondaryUser: User,
  storedSessionId: string,
): Promise<VerifiedImpersonationSession> => {
  const primaryUser = primaryServices.auth.currentUser
  if (!primaryUser) throw new Error('Primary actor is signed out.')

  const app = getOrCreateImpersonationApp()
  const callable = httpsCallable<void, ValidationResponse>(
    getFunctions(app),
    'validateImpersonationSessionCallable',
  )
  return validateIdentityBinding({
    primaryUid: primaryUser.uid,
    secondaryUser,
    storedSessionId,
    validateSession: async () => (await callable()).data,
  })
}

const useIdentityProvider = () => {
  useEffect(() => {
    let secondaryUnsubscribe: (() => void) | undefined
    let validationTimer: ReturnType<typeof setInterval> | undefined
    let restoreStarted = false
    let validationInFlight = false
    let cancelled = false

    const invalidate = async () => {
      if (cancelled) return
      try {
        await stopImpersonation('error')
      } catch {
        await clearLocalImpersonation()
      }
    }

    const restore = async () => {
      if (restoreStarted) return
      restoreStarted = true
      const storedSessionId = getStoredImpersonationSessionId()
      if (!storedSessionId) return
      useStore.setState({ identityStatus: 'verifying' })
      const app = getOrCreateImpersonationApp()
      const secondaryAuth = getImpersonationAuth()
      if (!secondaryAuth) return invalidate()

      const verifyUser = async (secondaryUser: User | null) => {
        if (!secondaryUser) return invalidate()
        if (validationInFlight) return
        validationInFlight = true
        try {
          const session = await validate(secondaryUser, storedSessionId)
          if (cancelled) return
          const current = useStore.getState()
          const alreadyActive =
            current.identityStatus === 'impersonating' &&
            current.impersonationSession?.sessionId === session.sessionId &&
            current.effectiveIdentity?.uid === session.targetUid

          if (!alreadyActive) {
            await clearPatientQueryCache()
            resetStore()
            activateEffectiveServices(app, session.sessionId, session.targetUid)
          }
          useStore.setState({
            effectiveIdentity: {
              uid: session.targetUid,
              source: 'impersonation',
            },
            impersonationSession: session,
            identityStatus: 'impersonating',
          })
        } catch (error) {
          if (isTerminalValidationError(error)) {
            await invalidate()
          } else {
            console.warn(
              'Impersonation validation temporarily failed; retaining the active session.',
              error,
            )
          }
        } finally {
          validationInFlight = false
        }
      }
      secondaryUnsubscribe = onAuthStateChanged(secondaryAuth, verifyUser)
      validationTimer = setInterval(() => {
        if (secondaryAuth.currentUser) {
          void verifyUser(secondaryAuth.currentUser)
        }
      }, 15_000)
    }

    const primaryUnsubscribe = onAuthStateChanged(
      primaryServices.auth,
      (actor) => {
        if (!getStoredImpersonationSessionId()) return
        if (!actor) {
          void stopImpersonation('logout', false).catch(() =>
            clearLocalImpersonation(),
          )
          return
        }
        if (!restoreStarted) void restore()
      },
    )

    const handleStorage = (event: StorageEvent) => {
      const action = crossTabIdentityAction(
        event.key,
        event.newValue,
        useStore.getState().impersonationSession?.sessionId,
      )
      if (action === 'clear-and-reload') {
        void clearLocalImpersonation().finally(() => window.location.reload())
      } else if (action === 'reload') {
        window.location.reload()
      }
    }
    window.addEventListener('storage', handleStorage)

    if (primaryServices.auth.currentUser && !restoreStarted) void restore()
    return () => {
      cancelled = true
      primaryUnsubscribe()
      secondaryUnsubscribe?.()
      if (validationTimer) clearInterval(validationTimer)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])
}

export default useIdentityProvider
